import React, { Dispatch, useEffect, useState } from 'react';
import styles from './OAuthLogins.module.scss';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { AuthPlatform, beginAuthWorkflow } from '@repo/utils/helpers/jwt';
import { lastValueFrom } from 'rxjs';
import { openUrlOutsideElectron } from '@repo/utils/helpers/electron-external';
import { AppleLoginButton, GoogleLoginButton } from '../Buttons';
import { useLocation } from 'react-router-dom';
import { setAppStartRoute } from '@repo/utils/helpers/app-start-route';
import analytics from '@repo/utils/helpers/analytics';
// import { isWindows } from '@support/platform-checks';

const AuthAlert = () => {
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const alerts = window.alerts || [];
    for (const alert of alerts) {
      setAlert(alert);
    }
  }, []);

  return (
    alert && (
      <div className={styles.alert}>
        <span className={`text-${alert.type}`}>{alert.message}</span>
      </div>
    )
  );
};

export function loginWithProvider(
  setSigningInWith: Dispatch<AuthPlatform>,
  platform: AuthPlatform,
  isRegister?: boolean,
) {
  setSigningInWith(platform);
  lastValueFrom(beginAuthWorkflow(platform, isRegister))
    .then(({ redirect }) => {
      if (
        window.app.isElectron &&
        window.desktopIntegrations?.appInfo.isMAS &&
        // !isWindows() &&
        platform === 'apple' &&
        !!window.desktopIntegrations?.loginOnMacWithApple
      ) {
        window.desktopIntegrations
          .loginOnMacWithApple(isRegister)
          .then(() => {
            return;
          })
          .catch(() => {
            setSigningInWith(null);
          });
        return;
      } else if (
        window.app.isElectron &&
        window.desktopIntegrations?.appInfo.os === 'macos' &&
        !!window.desktopIntegrations?.loginOnMac
      ) {
        window.desktopIntegrations.loginOnMac(redirect);
      } else if (window.app.isElectron) {
        openUrlOutsideElectron(redirect);
      } else {
        window.location.href = redirect;
      }
      setSigningInWith(null);
    })
    .catch((err) => {
      console.error(err);
      setSigningInWith(null);
    });
}

export const OAuthLogins = ({
  signingInWith,
  lastProvider,
  userHasExistingAccount,
  setSigningInWith,
}) => {
  const { pathname } = useLocation();

  const signInButtons = (
    <div className={styles.authChoices}>
      <AuthAlert />
      <AppleLoginButton
        onClick={() => onAuthClick('apple')}
        inProgress={signingInWith === 'apple'}
        disabled={!!signingInWith}
        showLastUsedTag={userHasExistingAccount && lastProvider === 'apple'}
      />
      <GoogleLoginButton
        onClick={() => onAuthClick('google')}
        inProgress={signingInWith === 'google'}
        disabled={!!signingInWith}
        showLastUsedTag={userHasExistingAccount && lastProvider === 'google'}
      />
    </div>
  );

  const signUpButtons = (
    <div className={styles.authChoices}>
      <AuthAlert />
      <AppleLoginButton
        onClick={() => onAuthClick('apple', true)}
        inProgress={signingInWith === 'apple'}
        disabled={!!signingInWith}
        isSignUp
      />
      <GoogleLoginButton
        onClick={() => onAuthClick('google', true)}
        inProgress={signingInWith === 'google'}
        disabled={!!signingInWith}
        isSignUp
      />
    </div>
  );

  function onAuthClick(platform: AuthPlatform, isRegister?: boolean) {
    if (!!signingInWith) {
      return;
    }

    if (!pathname.startsWith('/login')) {
      setAppStartRoute(pathname);
    }

    // handle signups from birthday
    if (!!localStorage.getItem('signup_from_birthday')) {
      localStorage.removeItem('signup_from_birthday');
      analytics.track('Sign-up started from birthday submission');
    }

    loginWithProvider(setSigningInWith, platform, isRegister);
  }

  return (
    <StateBasedFadeTransition
      state={String(userHasExistingAccount)}
      elementByStateMap={{
        true: signInButtons,
        false: signUpButtons,
      }}
      duration="medium"
    />
  );
};
